import axios, { AxiosError } from 'axios';
import type { SaveActionRequest, SaveActionResponse, CommercialAction } from '@/types';
import { auth, appCheck } from '@/config/firebase';
import { getToken } from 'firebase/app-check';

const API_BASE_URL = import.meta.env.VITE_CLOUD_RUN_API_URL;

// Modo desarrollo: si no hay API_BASE_URL configurada, usar modo mock
const DEVELOPMENT_MODE = !API_BASE_URL || API_BASE_URL === 'https://your-service-xxxxx.run.app';

if (DEVELOPMENT_MODE) {
  console.warn('⚠️ API Service en modo DESARROLLO - Las acciones no se guardarán en SAP');
}

// Crear instancia de axios con configuración base
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 segundos
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token de autenticación
apiClient.interceptors.request.use(
  async (config) => {
    config.headers = config.headers ?? {};

    const currentUser = auth.currentUser;
    if (currentUser) {
      try {
        const idToken = await currentUser.getIdToken();
        if (idToken) {
          (config.headers as any).Authorization = `Bearer ${idToken}`;
        }
      } catch (error) {
        console.warn('No se pudo obtener el ID token de Firebase Auth:', error);
      }
    }

    if (appCheck) {
      try {
        const { token } = await getToken(appCheck, false);
        if (token) {
          (config.headers as any)['X-Firebase-AppCheck'] = token;
        }
      } catch (error) {
        console.warn('No se pudo obtener el token de Firebase App Check:', error);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejo de errores
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// ============================================================================
// API METHODS
// ============================================================================

/**
 * Guarda o actualiza una acción comercial en SAP/Firestore
 */
export async function saveActionToSAP(
  countryId: string,
  clientId: string,
  action: Partial<CommercialAction>
): Promise<SaveActionResponse> {
  // MODO DESARROLLO: Simular respuesta exitosa
  if (DEVELOPMENT_MODE) {
    console.log('💾 [DEV MODE] Simulando guardado de acción:', { countryId, clientId, action });
    
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return {
      success: true,
      sapCode: action.sapCode || `SAP-DEV-${Date.now()}`,
      message: 'Acción guardada (modo desarrollo)',
    };
  }

  try {
    const payload: SaveActionRequest = {
      countryId,
      clientId,
      action,
    };

    const response = await apiClient.post<SaveActionResponse>(
      '/api/actions/save',
      payload
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.message || 'Error al guardar la acción';
      throw new Error(message);
    }
    throw error;
  }
}

/**
 * Elimina una acción comercial
 */
export async function deleteActionFromSAP(
  countryId: string,
  clientId: string,
  actionId: string
): Promise<SaveActionResponse> {
  // MODO DESARROLLO
  if (DEVELOPMENT_MODE) {
    console.log('🗑️ [DEV MODE] Simulando eliminación:', { countryId, clientId, actionId });
    await new Promise(resolve => setTimeout(resolve, 300));
    
    return {
      success: true,
      message: 'Acción eliminada (modo desarrollo)',
    };
  }

  try {
    const response = await apiClient.delete<SaveActionResponse>(
      `/api/actions/${actionId}`,
      {
        data: { countryId, clientId },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.message || 'Error al eliminar la acción';
      throw new Error(message);
    }
    throw error;
  }
}

/**
 * Sincroniza todas las acciones de un cliente con SAP
 */
export async function syncActionsWithSAP(
  countryId: string,
  clientId: string
): Promise<SaveActionResponse> {
  // MODO DESARROLLO
  if (DEVELOPMENT_MODE) {
    console.log('🔄 [DEV MODE] Simulando sincronización:', { countryId, clientId });
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return {
      success: true,
      message: 'Acciones sincronizadas (modo desarrollo)',
    };
  }

  try {
    const response = await apiClient.post<SaveActionResponse>(
      '/api/actions/sync',
      { countryId, clientId }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.message || 'Error al sincronizar acciones';
      throw new Error(message);
    }
    throw error;
  }
}

/**
 * Valida una acción antes de guardarla
 */
export async function validateAction(
  action: Partial<CommercialAction>
): Promise<{ valid: boolean; errors?: string[] }> {
  // MODO DESARROLLO
  if (DEVELOPMENT_MODE) {
    console.log('✅ [DEV MODE] Simulando validación:', action);
    await new Promise(resolve => setTimeout(resolve, 200));
    
    return {
      valid: true,
      errors: [],
    };
  }

  try {
    const response = await apiClient.post<{ valid: boolean; errors?: string[] }>(
      '/api/actions/validate',
      action
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        valid: false,
        errors: [error.response?.data?.message || 'Error al validar la acción'],
      };
    }
    throw error;
  }
}

// ============================================================================
// SYNC ALL TO SAP (Firebase Cloud Function, no es parte del Cloud Run API)
// ============================================================================

export interface SyncAllToSAPResult {
  success: boolean;
  dry_run?: boolean;
  count: number;
  by_table?: { CA: number; MT: number };
  updated?: number;
  /** Nº de documentos que quedaron pendientes (syncedToSAP:false) por errores de SAP en algún chunk. */
  failed?: number;
  /** Detalle de los chunks que fallaron al enviarse a SAP (rango, refs afectadas, error). */
  chunk_errors?: { range: string; refs: string[]; error: string }[];
  /** Mes reconstruido ('YYYY-MM'); ausente si se sincronizó el mes en curso. */
  period?: string;
  /** Documentos NO enviados a SAP por riesgo de corromperlo (p.ej. país sin isoCode válido). */
  skipped?: { path: string; reason: string }[];
  payload_preview?: { CA: unknown[]; MT: unknown[] };
  message: string;
  error?: string;
}

const FUNCTIONS_BASE_URL = `https://us-central1-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`;

async function callBulkSyncFunction(
  functionName: 'syncAllToSAP' | 'syncMitigationToSAP' | 'syncPipelineToSAP',
  dryRun: boolean,
  onlyUnsynced: boolean,
  table?: 'CA' | 'MT',
  period?: string
): Promise<SyncAllToSAPResult> {
  if (!appCheck) {
    throw new Error('Firebase App Check no está inicializado; no se puede ejecutar la sincronización.');
  }

  const { token } = await getToken(appCheck, false);

  const body: Record<string, unknown> = { dry_run: dryRun, only_unsynced: onlyUnsynced };
  if (table) body.table = table;
  if (period) body.period = period;

  const response = await fetch(`${FUNCTIONS_BASE_URL}/${functionName}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-firebase-appcheck': token,
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.error || `Error ${response.status} al sincronizar con SAP`);
  }

  return data as SyncAllToSAPResult;
}

/**
 * Dispara la sincronización masiva Firestore -> SAP (función `syncAllToSAP`).
 * Requiere un token de Firebase App Check válido, que se obtiene aquí mismo
 * a partir de la sesión activa en la app (no requiere manipular tokens a mano).
 *
 * @param dryRun si es true, SAP no recibe nada: solo se devuelve una vista previa del payload.
 * @param onlyUnsynced si es true, solo se envían los documentos con syncedToSAP === false.
 * @param table si se indica ('CA' o 'MT'), sincroniza solo esa tabla; si se omite, sincroniza ambas.
 * @param period 'YYYY-MM' para reconstruir el snapshot de ese mes en SAP; si se omite, el mes en curso.
 */
export async function syncAllToSAP(
  dryRun: boolean,
  onlyUnsynced: boolean = false,
  table?: 'CA' | 'MT',
  period?: string
): Promise<SyncAllToSAPResult> {
  return callBulkSyncFunction('syncAllToSAP', dryRun, onlyUnsynced, table, period);
}

/** Sincroniza SOLO Mitigation (TABLE='MT') con SAP, vía la función dedicada `syncMitigationToSAP`. */
export async function syncMitigationToSAP(
  dryRun: boolean,
  onlyUnsynced: boolean = false,
  period?: string
): Promise<SyncAllToSAPResult> {
  return callBulkSyncFunction('syncMitigationToSAP', dryRun, onlyUnsynced, undefined, period);
}

/** Sincroniza SOLO Commercial Action Tracker / pipeline (TABLE='CA') con SAP, vía la función dedicada `syncPipelineToSAP`. */
export async function syncPipelineToSAP(
  dryRun: boolean,
  onlyUnsynced: boolean = false,
  period?: string
): Promise<SyncAllToSAPResult> {
  return callBulkSyncFunction('syncPipelineToSAP', dryRun, onlyUnsynced, undefined, period);
}

/**
 * Health check del API
 */
export async function healthCheck(): Promise<boolean> {
  // MODO DESARROLLO
  if (DEVELOPMENT_MODE) {
    console.log('💚 [DEV MODE] Health check OK');
    return true;
  }

  try {
    const response = await apiClient.get('/health');
    return response.status === 200;
  } catch (error) {
    console.error('Health check failed:', error);
    return false;
  }
}

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

const apiService = {
  saveActionToSAP,
  deleteActionFromSAP,
  syncActionsWithSAP,
  syncAllToSAP,
  syncMitigationToSAP,
  syncPipelineToSAP,
  validateAction,
  healthCheck,
};

export default apiService;
